import { Button, Chip } from '@nyce/admin-kit/ui';
import { READ_SCOPE, WRITE_SCOPE, matchesScope } from '../../identity/api';
import { apiBaseUrl, CLIENT_VERSION, currentEnvironment, logout, useLogin } from '../../identity/session';
import { fmtDate } from '../../identity/format';
import { Code, Page } from '../../components/common';

/** What the current token says and what it lets this console do. */
export function AccessPage() {
  const login = useLogin();
  if (!login) return null;
  const a = login.access;
  return (
    <Page
      title="Access"
      description="The claims of the token this console signed in with. Authority comes from the tenant's claims, never from the identity provider."
      actions={
        <Button variant="ghost" onClick={() => void logout()}>
          Sign out
        </Button>
      }
    >
      <div className="nyk-card">
        <div className="nyk-card-head">
          <h2>Console permissions</h2>
        </div>
        <div className="nyk-inline">
          <Chip selected={a.canRead}>
            {a.canRead ? '✓' : '✗'} {READ_SCOPE}
          </Chip>
          <Chip selected={a.canWrite}>
            {a.canWrite ? '✓' : '✗'} {WRITE_SCOPE}
          </Chip>
        </div>
        {!a.canWrite && (
          <p className="nyk-card-intro" style={{ marginTop: 12, marginBottom: 0 }}>
            Read only: mutations are hidden. Add a <code>scope</code> claim <code>{WRITE_SCOPE}</code> (or{' '}
            <code>auth:tenants:*</code>) to tenant <code>{login.tenantId}</code> and sign in again.
          </p>
        )}
      </div>
      <div className="nyk-card">
        <div className="nyk-card-head">
          <h2>Token</h2>
        </div>
        <table className="nyk-kv" data-testid="token-claims">
          <tbody>
            <tr>
              <th>sub</th>
              <td>
                <Code>{a.subject}</Code>
              </td>
            </tr>
            <tr>
              <th>tid</th>
              <td>
                <Code>{a.tenantId ?? ''}</Code>
              </td>
            </tr>
            <tr>
              <th>orgid</th>
              <td>{a.orgId ?? <span className="nyk-ui-hint">—</span>}</td>
            </tr>
            <tr>
              <th>scope</th>
              <td>
                <div className="nyk-inline">
                  {a.scopes.map((s) => (
                    <Chip
                      key={s}
                      size="sm"
                      selected={matchesScope([s], READ_SCOPE) || matchesScope([s], WRITE_SCOPE)}
                    >
                      {s}
                    </Chip>
                  ))}
                  {!a.scopes.length && <span className="nyk-ui-hint">—</span>}
                </div>
              </td>
            </tr>
            <tr>
              <th>roles</th>
              <td>{a.roles.length ? a.roles.join(', ') : <span className="nyk-ui-hint">—</span>}</td>
            </tr>
            <tr>
              <th>role</th>
              <td>
                {a.identityRoles.length ? a.identityRoles.join(', ') : <span className="nyk-ui-hint">—</span>}
              </td>
            </tr>
            <tr>
              <th>exp</th>
              <td>{a.expiresAt ? fmtDate(new Date(a.expiresAt).toISOString()) : '—'}</td>
            </tr>
          </tbody>
        </table>
        <p className="nyk-ui-hint" style={{ marginTop: 12 }}>
          {currentEnvironment().label ? `${currentEnvironment().label} · ` : ''}API {apiBaseUrl()} · build{' '}
          {CLIENT_VERSION}
        </p>
      </div>
    </Page>
  );
}
